import { Component, Inject, ChangeDetectorRef, Injector, Input, SimpleChanges } from '@angular/core';
import { MatBottomSheetRef, MAT_BOTTOM_SHEET_DATA } from '@angular/material/bottom-sheet';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { environment } from '@environment';

import { takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';

import { BaseComponent } from '@core/components/base/base.component';

import { CartService } from '@core/services/cart/cart.service';
import { SessionStorageService } from '@core/services/util/session-storage.service';
import { OutletService } from '@core/services/outlet/outlet.service';
import { MEMBERSHIP_ACTION, MultiBrandPassService } from '@core/services/multi-brand-pass/multi-brand-pass.service';
import { DeferImageLoadDirective } from '../../directives/defer-image-load.directive';
import { PonchoUIModule } from '@poncho/ui';
import { InstallEatclubComponent } from '../install-eatclub/install-eatclub.component';
import { MatIconModule } from '@angular/material/icon';
import { NgClass, NgTemplateOutlet } from '@angular/common';

@Component({
    selector: 'app-multi-brand-pass-modal',
    templateUrl: './multi-brand-pass-modal.component.html',
    styleUrls: ['./multi-brand-pass-modal.component.scss'],
    standalone: true,
    imports: [NgClass, MatIconModule, InstallEatclubComponent, NgTemplateOutlet, PonchoUIModule, DeferImageLoadDirective]
})
export class MultiBrandPassModalComponent extends BaseComponent {
    brand_icons: any;
    selected: any;
    branding_text: any;
    passes = [];
    eatclubTrialExpired = false;
    outlet: any;
    isDefaultOutlet = false;
    passAvailable = false;
    showLoader = false;
    cartPass: any;
    customerSavings = 0;
    dialogBoxRef: MatDialogRef<any, any>;
    requestFromCart = false;
    pricesById = {};
    bestValuePass = {};
    @Input() brandingPageRequest = false;
    @Input() customerPassDetails: any = {};
    stage = '';
    ctaState: string = MEMBERSHIP_ACTION.APPLY_EATCLUB_AND_PROCEED;
    destroySubject$: Subject<void> = new Subject();

    constructor(
        @Inject(MAT_BOTTOM_SHEET_DATA) public data: any,
        private dialogRef: MatBottomSheetRef<MultiBrandPassModalComponent>,
        private cartService: CartService,
        private sessionStorage: SessionStorageService,
        private outletService: OutletService,
        private multiBrandPassService: MultiBrandPassService,
        private cdr: ChangeDetectorRef,
        private injector: Injector,
        private router: Router
    ) {
        super();
        if (!this.isMobile) {
            this.data = this.injector.get(MAT_DIALOG_DATA, null);
            this.dialogBoxRef = this.injector.get(MatDialogRef, null);
        }
    }

    ngOnChanges(changes: SimpleChanges) {
        const change = changes.customerPassDetails;
        if (change) {
            this.customerPassDetails = change.currentValue;
            this.hydratePassInfo();
        }
    }

    ngOnInitBrowser() {
        this.outlet = this.outletService.state.value.outlet;
        this.isDefaultOutlet = this.outlet.id === 1;
        this.requestFromCart = this.data?.page === 'cart';
        this.customerPassDetails = this.sessionStorage.get('passes') || this.customerPassDetails;
        this.hydratePassInfo();

        this.cartService.state.pipe(takeUntil(this.destroySubject$)).subscribe(() => {
            this.init(); // update pass selection on membership screen when changed from cart drop down
        });
    }

    ngOnDestroy() {
        this.destroySubject$.next();
        this.destroySubject$.complete();
    }

    hydratePassInfo() {
        this.passes = this.multiBrandPassService.createPassData(this.customerPassDetails?.all_passes);
        this.passAvailable = !!this.passes?.length;
        const { eatclub_trial_expired } = this.customerPassDetails;
        this.bestValuePass = this.multiBrandPassService.getBestValuePassId(this.passes, eatclub_trial_expired);
        this.refreshPage();
    }

    refreshPage() {
        if (this.customerPassDetails && Object.keys(this.customerPassDetails).length) {
            this.getPageDetails(this.customerPassDetails);
            this.customerSavings = this.customerPassDetails.customer_pass_details?.money_saved ?? 0;
        }
    }

    getPageDetails(data) {
        const {
            participating_brands,
            branding_text,
            eatclub_trial_expired,
            customer_pass_details: { stage }
        } = data;
        this.brand_icons = this.multiBrandPassService.getBrandIcons(participating_brands);
        this.branding_text = branding_text;
        this.eatclubTrialExpired = eatclub_trial_expired;
        this.stage = this.multiBrandPassService.getCustomerPassStage(stage);
        this.init();
    }

    init() {
        if (this.passAvailable) {
            this.cartPass = this.getCartPassIfExists();

            if (this.cartPass) {
                this.selectPass(this.cartPass);
                return;
            }

            const dfltPass = this.multiBrandPassService.getDfltPass(this.passes, this.eatclubTrialExpired);

            if (dfltPass) {
                this.selectPass(dfltPass);
            }
        }
    }

    getCartPassIfExists() {
        const cart = this.cartService.state.value;
        const items = cart && cart.items;
        const passes = this.customerPassDetails.all_passes;
        return this.multiBrandPassService.getSubItemPresentInCart(passes, items);
    }

    showDownloadEatclub(pass) {
        return !this.eatclubTrialExpired && pass.trial && environment.brand.id !== 19;
    }

    selectPass(pass) {
        this.ctaState = this.multiBrandPassService.getCTAText(this.isDefaultOutlet, this.stage, pass?.trial);
        if (this.isDefaultOutlet) {
            return;
        }

        this.selected = pass;
        this.cdr.detectChanges();
    }

    clickAction() {
        switch (this.ctaState) {
            case MEMBERSHIP_ACTION.EXPLORE_BRANDS:
                this.exploreBrands();
                break;
            case MEMBERSHIP_ACTION.DOWNLOAD_EATCLUB:
                this.installEatclub();
                break;
            default:
                this.joinNow();
                break;
        }
    }

    exploreBrands() {
        if (this.brandingPageRequest) {
            this.router.navigateByUrl('/');
            return;
        }
        this.close();
    }

    installEatclub() {
        if (this.isServer) {
            return;
        }

        const userAgent = navigator.userAgent;
        if (/iPad|iPhone|iPod|Mac/.test(userAgent)) {
            window.open(environment.app_link.apple);
        } else {
            window.open(environment.app_link.google);
        }
    }

    joinNow() {
        this.showLoader = true;

        if (!this.selected) {
            this.showLoader = false;
            return;
        }
        if (this.selected.id !== this.cartPass?.id) {
            this.cartService.addPass(this.selected);
        }

        this.showLoader = false;
        if (this.brandingPageRequest) {
            this.cartService.submitCart();
        } else {
            this.close('selected');
        }
    }

    close(mode?: string) {
        if (!this.isMobile) {
            this.dialogBoxRef.close(mode);
        } else {
            this.dialogRef.dismiss(mode);
        }
    }

    isSelected(pass) {
        if (!this.selected) {
            return false;
        }
        return pass.id === this.selected.id;
    }
}
